// FILE: src/components/AcceptInvitation.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
} from "firebase/auth";


const BRAND_LOGO = "/Lastwishbox-logo.png";
const STASH_KEY = "amInviteAcceptPending";

function parseJsonSafe(text) { try { return JSON.parse(text); } catch { return null; } }
async function getJson(url) {
  const res = await fetch(url, { method: "GET", credentials: "include" });
  const text = await res.text();
  const data = parseJsonSafe(text);
  return { ok: res.ok, status: res.status, data, raw: text };
}
async function postJson(url, body, idToken, extraHeaders) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(extraHeaders || {}),
    },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const text = await res.text();
  const data = parseJsonSafe(text);
  return { ok: res.ok, status: res.status, data, raw: text };
}

export default function AcceptInvitation() {
  const rawSearch = typeof window !== "undefined" ? window.location.search : "";
  const rawPath = typeof window !== "undefined" ? window.location.pathname : "";
  const params = useMemo(() => new URLSearchParams(rawSearch), [rawSearch]);

  const inviteId = useMemo(() => {
    const fromQuery = params.get("id") || params.get("inviteId") || "";
    if (fromQuery) return fromQuery;

    const m = rawPath.match(/\/invite\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : "";
  }, [params, rawPath]);

  const token    = useMemo(() => params.get("token") || "", [params]);

  const [ui, setUi] = useState({
    loading: true,
    error: "",
    email: "",
    accountId: "",
    accountContext: "business",
    code: (params.get("code") || "").trim(),
    pw1: "",
    pw2: "",
    busy: false,
    verified: false,
    verifying: false,
  });

  const [mode, setMode] = useState("choose");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUi((s) => ({ ...s, loading: true, error: "" }));
      try {
        if (!inviteId || !token) throw new Error("Invite link is missing details.");
        const url = `/api/accountManagers/preview?id=${encodeURIComponent(inviteId)}&token=${encodeURIComponent(token)}`;
        const { ok, data, raw } = await getJson(url);
        if (!ok || !data?.item?.email) {
          if (raw && /^<!doctype html/i.test(raw)) {
            throw new Error("API unreachable: /api/accountManagers/preview returned HTML. Check Hosting rewrites.");
          }
          throw new Error(data?.error || "We couldn’t load your invitation.");
        }
        if (!cancelled) {
          setUi((s) => ({
            ...s,
            loading: false,
            email: data.item.email,
            accountId: data.item.accountId || "",
            accountContext: data.item.accountContext || "business",
          }));
        }
      } catch (e) {
        if (!cancelled) setUi((s) => ({ ...s, loading: false, error: e?.message || "Failed to load invite." }));
      }
    })();
    return () => { cancelled = true; };
  }, [inviteId, token]);

  // Resume accept after login (when we stashed code pre-login)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stashRaw = sessionStorage.getItem(STASH_KEY);
        if (!stashRaw) return;

        // Firebase restores the session asynchronously, so currentUser is often
        // still null on mount. Returning here silently abandoned the stashed
        // code and left the invitation unaccepted. Wait for the first auth
        // state to settle instead.
        if (!auth.currentUser) {
          const user = await new Promise((resolve) => {
            const unsub = onAuthStateChanged(auth, (u) => { unsub(); resolve(u); });
          });
          if (!user) return;
        }
        if (cancelled) return;
        const stash = parseJsonSafe(stashRaw);
        if (!stash?.id || !stash?.token || !stash?.code) return;

        setUi((s) => ({ ...s, busy: true, error: "" }));
        const idToken = await auth.currentUser.getIdToken();
        const r = await postJson("/api/accountManagers/accept", {
          id: stash.id, token: stash.token, code: stash.code,
        }, idToken);

        if (!r.ok) {
          if (r.raw && /^<!doctype html/i.test(r.raw)) {
            throw new Error("API unreachable: /api/accountManagers/accept returned HTML. Check Hosting rewrites.");
          }
          throw new Error(r.data?.error || "Invite acceptance failed.");
        }

        // Profile creation handled securely by the backend amAccept function
        sessionStorage.removeItem(STASH_KEY);

        try { await auth.currentUser?.getIdToken(true); } catch {}

        if (!cancelled) window.location.assign("/manager-select");
      } catch (e) {
        setUi((s) => ({ ...s, busy: false, error: e?.message || "Could not accept the invitation." }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // IMPORTANT CHANGE:
  // We no longer ping the accept endpoint from here (it needs a Bearer token).
  // Instead we just mark the code as "ready". The real server validation happens
  // on the authenticated accept call.
  const verifyCode = useCallback(async () => {
    if (!ui.code) return;
    setUi((s) => ({ ...s, verifying: true, error: "" }));
    try {
      // Lightweight local check; server validates during final accept
      await new Promise((r) => setTimeout(r, 300));
      setUi((s) => ({ ...s, verifying: false, verified: true }));
    } catch (e) {
      setUi((s) => ({ ...s, verifying: false, verified: false, error: "Unable to verify code." }));
    }
  }, [ui.code]);

  function canProceed() {
    return !!ui.code && ui.verified && !ui.busy && !ui.loading && !ui.error;
  }

  async function acceptNowForSignedInUser() {
    setUi((s) => ({ ...s, busy: true, error: "" }));
    try {
      if (!auth.currentUser) throw new Error("Please log in first.");
      const idToken = await auth.currentUser.getIdToken();
      const r = await postJson("/api/accountManagers/accept", {
        id: inviteId, token, code: ui.code.trim(),
      }, idToken);

      if (!r.ok) {
        if (r.raw && /^<!doctype html/i.test(r.raw)) {
          throw new Error("API unreachable: /api/accountManagers/accept returned HTML. Check Hosting rewrites.");
        }
        throw new Error(r.data?.error || "Invite acceptance failed.");
      }

      try { await auth.currentUser?.getIdToken(true); } catch {}

      window.location.assign("/manager-select");
    } catch (e) {
      setUi((s) => ({ ...s, busy: false, error: e?.message || "Could not accept the invitation." }));
    }
  }

  function handleLoginChoice() {
    if (!canProceed()) return;
    if (auth.currentUser) {
      acceptNowForSignedInUser();
      return;
    }
    try {
      sessionStorage.setItem(STASH_KEY, JSON.stringify({
        id: inviteId, token, code: ui.code.trim(),
      }));
    } catch {}
    // Redirect back to this page after login so the resume effect can complete the accept
    const returnUrl = `/accept-invitation?id=${encodeURIComponent(inviteId)}&token=${encodeURIComponent(token)}`;
    // Must be /login, not /. The landing page ignores ?redirect, so the user
    // logged in and was never returned here, leaving the stashed code unused
    // and the invitation permanently unaccepted.
    window.location.assign(`/login?redirect=${encodeURIComponent(returnUrl)}`);
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!canProceed()) return;
    if (!ui.pw1 || ui.pw1.length < 8) return setUi((s) => ({ ...s, error: "Password must be at least 8 characters." }));
    if (ui.pw1 !== ui.pw2) return setUi((s) => ({ ...s, error: "Passwords do not match." }));

    setUi((s) => ({ ...s, busy: true, error: "" }));
    try {
      // Stash BEFORE any auth operations so the resume effect can
      // complete the flow if the component remounts due to auth state change
      try {
        sessionStorage.setItem(STASH_KEY, JSON.stringify({
          id: inviteId, token, code: ui.code.trim(),
          accountId: ui.accountId || null, isNewAccount: true,
        }));
      } catch {}

      // Create account (no signOut - that was causing the component to unmount)
      try { await createUserWithEmailAndPassword(auth, ui.email, ui.pw1); }
      catch (authErr) {
        if (authErr.code === "auth/email-already-in-use") {
          await signInWithEmailAndPassword(auth, ui.email, ui.pw1);
        } else {
          throw authErr;
        }
      }

      // If we get here the component survived the auth state change — complete inline
      const idToken = await auth.currentUser.getIdToken();
      const r = await postJson("/api/accountManagers/accept", {
        id: inviteId, token, code: ui.code.trim(),
      }, idToken);

      if (!r.ok) {
        if (r.raw && /^<!doctype html/i.test(r.raw)) {
          throw new Error("API unreachable: /api/accountManagers/accept returned HTML. Check Hosting rewrites.");
        }
        throw new Error(r.data?.error || "Invite acceptance failed.");
      }

      // Profile creation handled securely by the backend amAccept function
      sessionStorage.removeItem(STASH_KEY);
      try { await auth.currentUser?.getIdToken(true); } catch {}
      window.location.assign("/manager-select");
    } catch (e) {
      setUi((s) => ({ ...s, busy: false, error: e?.message || "Could not accept invitation." }));
    }
  }

  if (ui.loading) return null;

  return (
    <div style={{ minHeight: "100vh", display:"flex", alignItems:"center", justifyContent:"center", padding: 24, background:"#fafafa" }}>
      <div style={{ width:"100%", maxWidth:560 }}>
        <div style={{ textAlign:"center", marginBottom: 24 }}>
          <img src={BRAND_LOGO} alt="LastWishBox" style={{ height: 56, objectFit:"contain" }} />
        </div>

        <div style={{ background:"#fff", border:"1px solid #e6e6e6", borderRadius:16, boxShadow:"0 1px 6px rgba(0,0,0,.05)", padding:24 }}>
          <h2 style={{ textAlign:"center", margin:0 }}>
            {ui.accountContext === "personal"
              ? "Accept Personal Manager Invitation"
              : "Accept Business Manager Invitation"}
          </h2>
          <p style={{ textAlign:"center", color:"#666", marginTop:8 }}>
            {ui.accountContext === "personal"
              ? "Enter your one-time access code to continue to personal read-only access."
              : "Enter your one-time access code to continue to business read-only access."}
          </p>

          {ui.error && (
            <div style={{ marginTop: 12, color:"#b00020", textAlign:"center", fontSize:14 }}>
              {ui.error}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label style={{ display:"block", fontSize:13, marginBottom:4 }}>One-Time Access Code</label>
            <div className="accept-invite-code-row">
              <input
                type="text"
                required
                value={ui.code}
                onChange={(e) => setUi((s) => ({ ...s, code: e.target.value, verified: false }))}
                placeholder="Enter the code from your email"
                style={{ width:"100%", flex:1, padding:"10px 12px", border:"1px solid #ddd", borderRadius:8, letterSpacing:2, textAlign:"center" }}
              />
              <button
                type="button"
                className="accept-invite-verify-btn"
                onClick={verifyCode}
                disabled={!ui.code || ui.verifying}
                style={{
                  minWidth:112, padding:"10px 12px", borderRadius:8,
                  background: ui.verified ? "#16a34a" : "#111827", color:"#fff", border:"none",
                  cursor: (!ui.code || ui.verifying) ? "not-allowed" : "pointer", opacity: ui.verifying ? .7 : 1
                }}
              >
                {ui.verified ? "Verified" : (ui.verifying ? "Checking…" : "Verify")}
              </button>
            </div>
            <div style={{ fontSize:12, color: ui.verified ? "#16a34a" : "#666", marginTop:4 }}>
              {ui.verified ? "Code captured. We’ll validate it after you sign in." : "Must exactly match the code in your invite email."}
            </div>
          </div>

          {mode === "choose" && (
            <div style={{ display:"grid", gap:12, marginTop:16 }}>
              <button
                onClick={() => canProceed() && setMode("create")}
                disabled={!canProceed()}
                style={{
                  width:"100%", padding:"12px 14px", borderRadius:8,
                  background: canProceed() ? "#000" : "#999", color:"#fff",
                  border:"none", cursor: canProceed() ? "pointer" : "not-allowed", opacity: ui.busy ? 0.7 : 1
                }}
              >
                {ui.accountContext === "personal" ? "Create personal manager account" : "Create business manager account"}
              </button>
              <button
                onClick={handleLoginChoice}
                disabled={!canProceed()}
                style={{
                  width:"100%", padding:"12px 14px", borderRadius:8,
                  background: canProceed() ? "#f3f4f6" : "#f0f0f0",
                  border:"1px solid #e5e7eb", cursor: canProceed() ? "pointer" : "not-allowed"
                }}
              >
                {ui.accountContext === "personal" ? "Log in to access personal account" : "Log in to access business account"}
              </button>
              <div style={{ color:"#666", fontSize:12, textAlign:"center" }}>
                {ui.accountContext === "personal"
                  ? "You won’t be billed. This gives read-only personal access only, once enabled by the owner or unlock rules."
                  : "You won’t be billed. This gives read-only business access only, once enabled by the owner or unlock rules."}
              </div>
            </div>
          )}

          {mode === "create" && (
            <form onSubmit={handleCreate} style={{ marginTop: 16, display:"grid", gap:12 }}>
              <div>
                <label style={{ display:"block", fontSize:13, marginBottom:4 }}>Email</label>
                <input type="email" value={ui.email} readOnly autoComplete="username"
                  style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:8, background:"#f7f7f7", textAlign:"center" }}/>
              </div>
              <div>
                <label style={{ display:"block", fontSize:13, marginBottom:4 }}>Password</label>
                <input type="password" required minLength={8} value={ui.pw1}
                  onChange={(e) => setUi((s) => ({ ...s, pw1: e.target.value }))}
                  autoComplete="new-password" placeholder="Create a secure password"
                  style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:8 }}/>
              </div>
              <div>
                <label style={{ display:"block", fontSize:13, marginBottom:4 }}>Confirm password</label>
                <input type="password" required minLength={8} value={ui.pw2}
                  onChange={(e) => setUi((s) => ({ ...s, pw2: e.target.value }))}
                  autoComplete="new-password" placeholder="Re-enter your password"
                  style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:8 }}/>
              </div>
              <button
                type="submit"
                disabled={!canProceed() || ui.pw1.length < 8 || ui.pw1 !== ui.pw2}
                style={{
                  width:"100%", padding:"10px 12px", borderRadius:8, background:"#000", color:"#fff", border:"none",
                  opacity: ui.busy ? 0.7 : 1, cursor: (!canProceed() ? "not-allowed" : (ui.busy ? "progress" : "pointer"))
                }}
              >
                {ui.busy ? "Accepting…" : "Accept & Create Account"}
              </button>
              <button
                type="button"
                onClick={() => setMode("choose")}
                style={{ marginTop: 8, width:"100%", background:"transparent", border:"none", color:"#555", cursor:"pointer" }}
              >
                ← Back
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
